// @ts-check
import React, { useEffect, useState } from "react";

import { SubmitState } from "consts";
import { actions } from "actions";
import { connect } from "unistore/preact";
import { range } from "utils";
import i18n from "micro-i18n";
import { findOptionForAge, getInitialAgeFromOptions } from "utils/age";

/**
 * @template T
 * @typedef {import('preact').ComponentType<T>} ComponentType
 */

/**
 * @typedef {import('preact').JSX.Element} JSXElement
 * @typedef {import('types/api-responses').QuestionOption} QuestionOption
 */

/**
 * @typedef {{ value: number, label: string }} AgeEntry
 * @typedef {{ key: string, label: string, ages: AgeEntry[] }} Decade
 */

/**
 * @param {number} start
 * @param {number} count
 * @returns {AgeEntry[]}
 */
const agesFor = (start, count) => range(count, { start }).map((value) => ({ value, label: String(value) }));

/** @type {Decade[]} */
const DECADES = [
  { key: "13s", label: "13-17", ages: agesFor(13, 5) },
  { key: "18s", label: "18-24", ages: agesFor(18, 7) },
  { key: "25s", label: "25-34", ages: agesFor(25, 10) },
  { key: "35s", label: "35-44", ages: agesFor(35, 10) },
  { key: "45s", label: "45-54", ages: agesFor(45, 10) },
  { key: "55s", label: "55-64", ages: agesFor(55, 10) },
  { key: "65s", label: "65-74", ages: agesFor(65, 10) },
  { key: "75s", label: "75+", ages: [...agesFor(75, 15), { value: 90, label: "90 or above" }] },
];

/**
 * Locates the decade entry containing a given age. Used to restore the drilldown
 * to the correct decade when the user revisits a previously-answered question.
 *
 * @param {number} age
 * @returns {Decade | null}
 */
const findDecadeForAge = (age) => DECADES.find((d) => d.ages.some((a) => a.value === age)) ?? null;

/**
 * @typedef {Object} AgePickerQuestionProps
 * @property {import('types/api-responses').QuestionRadio} question
 * @property {string} labelId
 * @property {boolean} [isDisabled]
 * @property {(args: { option: QuestionOption, autoAdvance: boolean }) => void} onOptionChanged
 * @property {(submitState: number) => void} setSubmitState
 */

/**
 * @param {AgePickerQuestionProps} props
 * @returns {JSXElement}
 */
const AgePickerQuestion = ({ question, labelId, isDisabled = false, onOptionChanged, setSubmitState }) => {
  const initialAge = getInitialAgeFromOptions(question.options);
  const initialDecade = initialAge !== null ? findDecadeForAge(initialAge) : null;

  const [selectedDecade, setSelectedDecade] = useState(/** @type {Decade | null} */ (initialDecade));
  const [selectedAge, setSelectedAge] = useState(/** @type {number | null} */ (initialAge));

  useEffect(() => {
    if (initialAge !== null) {
      setSubmitState(SubmitState.Ready);
    }
  }, []);

  /** @param {Decade} decade */
  const handleDecadeClick = (decade) => {
    setSelectedDecade(decade);
    setSelectedAge(null);
  };

  const handleBack = () => {
    setSelectedDecade(null);
    setSelectedAge(null);
    setSubmitState(SubmitState.NotReady);
  };

  /** @param {number} age */
  const handleAgeSelect = (age) => {
    setSelectedAge(age);
    const option = findOptionForAge(age, question.options);
    if (option) {
      // onOptionChanged drives submitState to Auto for radio + autoAdvance — don't follow
      // up with setSubmitState(Ready) or it clobbers Auto and the Deck never auto-submits.
      onOptionChanged({ option, autoAdvance: true });
    }
  };

  if (!selectedDecade) {
    return (
      <div className="csw-age-picker-question" aria-labelledby={labelId}>
        <div className="csw-age-picker-decades">
          {DECADES.map((decade) => (
            <button
              key={decade.key}
              type="button"
              className="csw-btn csw-age-picker-decade-btn outline"
              onClick={() => handleDecadeClick(decade)}
              disabled={isDisabled}
            >
              {decade.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const colBreak = Math.ceil(selectedDecade.ages.length / 2);

  return (
    <div className="csw-age-picker-question" aria-labelledby={labelId}>
      <button
        type="button"
        className="csw-btn csw-btn-link csw-age-picker-back outline"
        onClick={handleBack}
        disabled={isDisabled}
      >
        {i18n.t("AGE_PICKER_BACK")}
      </button>
      <ul className="csw-q-rdo csw-age-picker-ages" style={{ gridTemplateRows: `repeat(${colBreak}, auto)` }}>
        {selectedDecade.ages.map(({ value, label }, index) => {
          const inputId = `csw-age-picker-${value}`;
          return (
            <li
              key={value}
              className={`csw-q-rdo-o${index === 0 || index === colBreak ? " csw-age-picker-col-start" : ""}`}
            >
              <input
                type="radio"
                className="csw-q-rdo-input outline"
                id={inputId}
                name="csw-age-picker"
                value={value}
                checked={selectedAge === value}
                onChange={() => handleAgeSelect(value)}
                disabled={isDisabled}
              />
              <label className="csw-q-rdo-lbl" htmlFor={inputId}>
                <span className="csw-q-rdo-chk" />
                <span className="csw-q-rdo-txt">{label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** @type {ComponentType<AgePickerQuestionProps>} */
export default connect("", actions)(AgePickerQuestion);
